import React, { useState } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import styles from "./styles.module.scss";
import NextBtn from "app/images/next-btn.svg";
import NextBtnJp from "app/images/next-btn-jp.svg";
import DetailsSession from "./DetailsSession";
import CancelConfirmation from "./CancelConfirmation";
import SessionParticipantBadge from "./SessionParticipantBadge";
import {
  participantFullName,
  sessionBadgeParticipants,
} from "./helpers/sessionParticipant";
import { cls } from "app/utils";
import { getPaymentIconClasses } from "app/utils/paymentUtils";
import { generateCurrencySymbol } from "app/routes/BuildProgram/Pricing/utils";
import { resourceLines, resourceLineText } from "app/utils/request/scheduling/facilityResources";
import ResourceIcon from "app/components/ResourceIcon";

export default function SessionCard({
  session,
  first,
  onEditSession,
  onCancelSession,
}: any) {
  const { t, i18n } = useTranslation();
  const [isModalOpen, setModalOpen] = useState(false);

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const startDate: any = DateTime.fromISO(session.start).setZone(
    session.timeZone,
  );
  const monthAbbr = startDate.toFormat("MMM").toUpperCase();
  const dayNumber = startDate.toFormat("d");
  const formattedDateFull = startDate.toLocaleString(DateTime.DATE_FULL);
  const startTime = session.startTimeString.replace(/^0/, "");
  const endTime = session.endTimeString.replace(/^0/, "");
  const timeZone = session.timeZoneShortString;

  // The bays the booking takes (decision Q18), one "Section: titles" line a
  // section. The list is empty while the facility has resource management
  // off, so the card shows no line then.
  const bayLines = resourceLines(session);

  const coachName =
    session.coachProfile.firstName + " " + session.coachProfile.lastName;
  const [cancelConfirmation, setCancelConfirmation] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // CFTB-3297 (+ CFTB-3336 follow-up): name everyone affected by the cancellation
  // — the attending dependents plus the head when they're also a participant — so
  // a "me + child" session names both. Mirrors the participant badge.
  const participantNames = sessionBadgeParticipants(session)
    .map((participant) => participantFullName(participant))
    .join(", ");

  return (
    <>
      <div className={styles.session_card} data-testid="session-card">
        <div className={styles.session_header}>
          <div className={styles.date_badge}>
            <span className={styles.date_badge_month}>{monthAbbr}</span>
            <span className={styles.date_badge_day}>{dayNumber}</span>
          </div>
          {first && (
            <img
              src={i18n.language === "en" ? NextBtn : NextBtnJp}
              className={styles.next_btn}
              alt="next"
            />
          )}
        </div>
        <div className={styles.session_title_row}>
          <div className={styles.session_title}>
            {session.lessonRateTitle || t("Custom Session")}
          </div>
          <i
            className={cls(
              ...getPaymentIconClasses(session?.payment_method, styles),
              styles.payment_icon,
              {
                [styles.pending]: session?.payment_status === "pending",
                [styles.failed]:
                  session?.payment_status === "past_due" ||
                  session?.payment_status === "failed",
              },
            )}
          />
        </div>
        <div className={styles.coach_row}>
          <img
            className={styles.coach_avatar}
            src={session.coachProfile.profilePicture}
            alt={coachName}
          />
          <span className={styles.coach_name}>{coachName}</span>
        </div>
        <SessionParticipantBadge session={session} className={styles.participant_badge} />
        <div className={styles.detail_row}>
          <i className="ico ico-calendar" />
          <span>{formattedDateFull}</span>
        </div>
        <div className={styles.detail_row}>
          <i className="ico ico-clock" />
          <span>{startTime} - {endTime} {timeZone}</span>
        </div>
        {bayLines.map((line) => (
          <div className={styles.detail_row} data-testid="session-card-resource" key={line.section}>
            <span className={styles.detail_row_icon}>
              <ResourceIcon size={14} />
            </span>
            <span>{resourceLineText(line)}</span>
          </div>
        ))}
        <div className={styles.horizontal_line} />
        <div className={styles.footer_card}>
          {/* CN-2711: the booking option hides its price — the card keeps its
              footer layout, minus the amount. */}
          <div className={styles.price}>
            {!session.lessonRateHidePrice && (
              <>
                {generateCurrencySymbol(session?.rateCurrency) || session?.coachProfile?.currencySymbol || "$"}
                {session.rate}
              </>
            )}
          </div>
          <div className={styles.details_btn} onClick={openModal} data-testid="session-card-details-btn">
            {t("Details")}
          </div>
        </div>
      </div>
      <DetailsSession
        isOpen={isModalOpen}
        onClose={closeModal}
        session={session}
        onEditSession={onEditSession}
        onCancelSession={() => {
          closeModal();
          setCancelConfirmation(true);
        }}
      />
      {cancelConfirmation && (
        <CancelConfirmation
          participantName={participantNames || undefined}
          loading={cancelling}
          onConfirm={() => {
            if (cancelling) {
              return;
            }
            setCancelling(true);
            Promise.resolve(
              onCancelSession(session, () => setCancelConfirmation(false)),
            ).finally(() => setCancelling(false));
          }}
          onClose={() => {
            setCancelConfirmation(false);
            openModal();
          }}
        />
      )}
    </>
  );
}
